(() => {
  if (window.blendFooterTransition) return;

  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  // Keep this in sync with the hidden watermark in section-footer.css.
  const hiddenWatermark = window.matchMedia('(max-width: 749px), (hover: none) and (pointer: coarse)');
  const surfaceSelector = [
    ':scope > .shopify-section',
    ':scope > .shopify-section > :not(style, script, link)',
    ':scope > .shopify-section > .isolate > .rich-text',
    ':scope > .shopify-section > .gradient > .isolate',
  ].join(', ');
  const nearbyTargets = new Set();
  let mainContent;
  let cta;
  let footer;
  let watermarkBottom = null;
  let frameId = null;
  let refreshNeeded = true;
  let surfacesNeeded = true;
  let isNearby = false;
  let isActive = false;

  // Read section canvases once as the footer approaches. Never alternate
  // computed-style/geometry reads with attribute writes inside this loop.
  const readSurfaces = () => {
    const surfaces = [...mainContent.querySelectorAll(surfaceSelector)];
    const participants = [];
    const whiteSurfaces = [];
    const pageWidth = mainContent.getBoundingClientRect().width;
    let hasWhiteSection = false;

    [mainContent, ...surfaces].forEach((surface) => {
      const style = getComputedStyle(surface);
      const channels = style.backgroundColor.match(/[\d.]+/g)?.map(Number);
      const [red = 0, green = 0, blue = 0, alpha = 1] = channels || [];
      const isWhite = channels?.length >= 3 && Math.min(red, green, blue) >= 240 &&
        Math.max(red, green, blue) - Math.min(red, green, blue) <= 10;
      const plainBackground = style.backgroundImage === 'none';

      // Cards, media, inputs and the black footer remain outside this shallow selection.
      if (surface !== mainContent && !surface.hasAttribute('data-blend-footer-surface') &&
          plainBackground && (alpha === 0 || isWhite)) {
        participants.push(surface);
      }

      // Remember white canvases while the footer's active CSS makes them transparent.
      const knownWhite = surface.dataset.blendFooterWhiteSurface === 'true';
      if (knownWhite || (plainBackground && isWhite && alpha >= 0.99)) {
        if (!knownWhite) whiteSurfaces.push(surface);
        if (!hasWhiteSection) {
          const rect = surface.getBoundingClientRect();
          hasWhiteSection = rect.height > 0 && rect.width >= pageWidth * 0.8;
        }
      }
    });

    // The home service section uses the main canvas, including its beige scroll state.
    const hasServiceCanvas = document.body.classList.contains('template-type-index') &&
      Boolean(mainContent.querySelector(':scope > .section--agency-services-projects'));
    return { participants, whiteSurfaces, continuesWhite: hasWhiteSection || hasServiceCanvas };
  };

  const intersectionObserver = typeof IntersectionObserver === 'function' ? new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      if (target !== cta && target !== footer) return;
      if (isIntersecting) nearbyTargets.add(target);
      else nearbyTargets.delete(target);
    });
    isNearby = nearbyTargets.size > 0;
    scheduleUpdate();
  }, { rootMargin: '600px 0px' }) : null;

  const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(() => scheduleVisibleUpdate()) : null;

  const refresh = () => {
    refreshNeeded = false;
    surfacesNeeded = true;
    intersectionObserver?.disconnect();
    resizeObserver?.disconnect();
    nearbyTargets.clear();
    isNearby = !intersectionObserver;
    mainContent = document.getElementById('MainContent');
    cta = document.querySelector('.blend-footer-entry [data-footer-cta]');
    footer = document.querySelector('.blend-footer');
    watermarkBottom = null;
    // MainContent's many image/font/layout changes do not require footer measurements.
    [cta, footer].forEach((element) => {
      if (!element) return;
      intersectionObserver?.observe(element);
      resizeObserver?.observe(element);
    });
  };

  const update = () => {
    frameId = null;
    if (refreshNeeded) refresh();
    const hasCta = mainContent && cta?.isConnected;
    if (!isNearby && !isActive) {
      if (!hasCta) {
        mainContent?.removeAttribute('data-blend-footer-transition');
        root.removeAttribute('data-blend-footer-focus');
      }
      return;
    }

    // Complete every layout/style read before applying any visual state below.
    const viewportHeight = window.visualViewport?.height || root.clientHeight;
    const viewportTop = window.visualViewport?.offsetTop || 0;
    const viewportBottom = viewportTop + viewportHeight;
    const surfaceState = hasCta && surfacesNeeded ? readSurfaces() : null;
    const ctaRect = hasCta ? cta.getBoundingClientRect() : null;
    // Stay active through the footer; scrolling up restores the original page canvas.
    const nextActive = Boolean(ctaRect && ctaRect.height > 0 && ctaRect.top <= viewportHeight * 0.8);
    let bottom = null;
    if (footer?.isConnected && !hiddenWatermark.matches) {
      bottom = 24;
      if (!reducedMotion.matches && isNearby) {
        const rect = footer.getBoundingClientRect();
        if (rect.top < viewportBottom && rect.bottom > viewportTop) {
          // Hold the logo in the viewport, then settle at the footer's bottom edge.
          bottom = Math.round(Math.max(24, rect.bottom - viewportBottom + 24) * 100) / 100;
        }
      }
    }

    if (surfaceState) {
      mainContent.setAttribute('data-blend-footer-transition', '');
      surfaceState.participants.forEach((surface) => surface.setAttribute('data-blend-footer-surface', ''));
      surfaceState.whiteSurfaces.forEach((surface) => { surface.dataset.blendFooterWhiteSurface = 'true'; });
      cta.toggleAttribute('data-continues-white-canvas', surfaceState.continuesWhite);
      surfacesNeeded = false;
    } else if (!hasCta) {
      mainContent?.removeAttribute('data-blend-footer-transition');
    }
    if (bottom !== watermarkBottom) {
      watermarkBottom = bottom;
      if (bottom === null) footer?.style.removeProperty('--blend-footer-watermark-bottom');
      else footer.style.setProperty('--blend-footer-watermark-bottom', `${bottom}px`);
    }
    if (nextActive !== isActive || (!hasCta && root.hasAttribute('data-blend-footer-focus'))) {
      isActive = nextActive;
      if (isActive) root.setAttribute('data-blend-footer-focus', 'true');
      else root.removeAttribute('data-blend-footer-focus');
    }
  };

  function scheduleUpdate() {
    if (frameId !== null) return;
    frameId = requestAnimationFrame(update);
  }

  function scheduleVisibleUpdate() {
    if (isNearby || isActive) scheduleUpdate();
  }

  const refreshAndUpdate = () => {
    refreshNeeded = true;
    scheduleUpdate();
  };
  const resizeAndUpdate = () => {
    surfacesNeeded = true;
    scheduleVisibleUpdate();
  };

  window.addEventListener('scroll', scheduleVisibleUpdate, { passive: true });
  ['resize', 'orientationchange'].forEach((event) => {
    window.addEventListener(event, resizeAndUpdate, { passive: true });
  });
  window.addEventListener('pageshow', refreshAndUpdate, { passive: true });
  window.visualViewport?.addEventListener('resize', scheduleVisibleUpdate, { passive: true });
  window.visualViewport?.addEventListener('scroll', scheduleVisibleUpdate, { passive: true });
  reducedMotion.addEventListener('change', scheduleVisibleUpdate);
  hiddenWatermark.addEventListener('change', scheduleVisibleUpdate);
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((event) => {
    document.addEventListener(event, refreshAndUpdate);
  });
  window.addEventListener('load', refreshAndUpdate, { once: true });
  document.fonts?.ready.then(resizeAndUpdate);
  window.blendFooterTransition = { refresh: refreshAndUpdate };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshAndUpdate, { once: true });
  } else {
    refreshAndUpdate();
  }
})();
